<template>
    <div class="mt-3">
        <div class="flex flex-col gap-3 md:grid md:grid-cols-3 md:items-center">
            <div class="hidden md:block"></div>
            <h1 class="text-3xl text-center">
                {{ pageTitle }}
                <strong class="text-theme-primary">
                    {{ itemsNumber }}
                </strong>
            </h1>
            <div class="flex justify-center md:justify-end">
                <app-button
                    v-if="$ability.hasPermission('order:Create')"
                    color="success"
                    size="large"
                    @click="isCreationModalOpen = true"
                >
                    <span class="hidden md:inline">Registrar nueva venta</span>
                    <span class="md:hidden font-bold">+</span>
                </app-button>
            </div>
        </div>

        <div class="mt-3">
            <orders-filters
                v-model="query"
                :enabled-filters="$data.$enabledFilters"
                @enter="reloadItems"
            />
            <div class="flex justify-center mt-3">
                <app-button v-show="query" relief @click="reloadItems">
                    Consultar 🔎
                </app-button>
            </div>
        </div>
        <h3
            v-if="totalCalls > 1 && selected.length"
            class="text-lg mt-2 text-theme-base"
        >
            Ordenes seleccionadas
            {{ selected.length }}
        </h3>
        <div
            class="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 my-4"
        >
            <div
                class="flex flex-wrap justify-center lg:justify-start items-center gap-2"
            >
                <advanced-options-button
                    v-if="
                        itemsCurrent.length > 0 ||
                        $ability.hasPermission('order:ListAll')
                    "
                    :options="advancedOptions"
                    @option-click="onAdvancedOptionClick"
                />
                <tooltip-button
                    v-if="selected.length"
                    icon="📕"
                    message="PDF de órdenes seleccionadas"
                    @click="downloadPDFComponent(true)"
                />
                <tooltip-button
                    v-show="selected.length"
                    icon="👀"
                    message="Vista previa de órdenes seleccionadas"
                    @click="openPreview"
                />
            </div>

            <div
                class="flex flex-wrap justify-center lg:justify-end items-center gap-4"
            >
                <list-settings-button
                    :value="{ pageSize, sortConfig }"
                    :sort-options="sortOptions"
                    @change="onListSettingsChange"
                />

                <custom-pagination
                    v-model="currentPage"
                    :length="pagesNumber"
                    @input="listItems()"
                />
            </div>
        </div>

        <div
            v-if="itemsCurrent.length && query.deliveryStatus === 'completed'"
            class="flex flex-wrap items-center gap-x-4 gap-y-1 px-3 py-2 mb-2 rounded-lg bg-theme-card border border-theme-border text-sm"
        >
            <span class="font-semibold text-theme-base mr-1">
                Ordenes completadas :
                <span class="text-theme-primary font-bold">
                    {{ itemsCurrent.length }}
                </span>
            </span>
            <span class="font-bold text-theme-primary">
                Total {{ money.total | money }}
            </span>
            <span v-if="money.cash" class="text-theme-muted">
                Efectivo:
                <span class="text-theme-base font-medium">{{
                    money.cash | money
                }}</span>
            </span>
            <span v-if="money.bre_b" class="text-theme-muted">
                Bre-b:
                <span class="text-theme-base font-medium">{{
                    money.bre_b | money
                }}</span>
            </span>
            <span v-if="money.daviplata" class="text-theme-muted">
                Daviplata:
                <span class="text-theme-base font-medium">{{
                    money.daviplata | money
                }}</span>
            </span>
            <span v-if="money.nequi" class="text-theme-muted">
                Nequi:
                <span class="text-theme-base font-medium">{{
                    money.nequi | money
                }}</span>
            </span>
            <span v-if="money.terminal" class="text-theme-muted">
                Datáfono:
                <span class="text-theme-base font-medium">{{
                    money.terminal | money
                }}</span>
            </span>
            <span v-if="money.terminal1" class="text-theme-muted">
                Datáfono 1:
                <span class="text-theme-base font-medium">{{
                    money.terminal1 | money
                }}</span>
            </span>
            <span v-if="money.terminal2" class="text-theme-muted">
                Datáfono 2:
                <span class="text-theme-base font-medium">{{
                    money.terminal2 | money
                }}</span>
            </span>
            <span v-if="money.terminal3" class="text-theme-muted">
                Datáfono 3:
                <span class="text-theme-base font-medium">{{
                    money.terminal3 | money
                }}</span>
            </span>
        </div>

        <div class="w-full mt-2">
            <custom-sortable-table
                ref="selectableTable"
                :fields="$data.$fields"
                :items="itemsCurrent"
                :row-actions="rowActions"
                :per-page="itemsCurrent.length"
                select-mode="multi"
                responsive
                outlined
                no-border-collapse
                hover
                selectable
                class="w-full"
                items-empty-message="Nuestro sistema NO encuentra resultados. Intenta modificar los filtros de búsqueda."
                @flex-selected="onRowSelected"
                @row-action="onRowAction"
            >
                <template #cell(id)="data">
                    <div class="flex items-center">
                        <router-link
                            :to="`/app/orders/detail/${data.item.id}`"
                            class="text-center btn btn-stack-primary p-1"
                        >
                            <span> {{ data.item.id }}</span>
                        </router-link>
                    </div>
                </template>
                <template #cell(deliveryStatus)="data">
                    <span
                        class="text-center"
                        :class="
                            $data.$ordersStatusesMessageClass[
                                data.item.deliveryStatus
                            ]
                        "
                    >
                        {{
                            $data.$ordersStatusesCodes[data.item.deliveryStatus]
                        }}
                    </span>
                </template>
                <template #cell(deliveryOption)="data">
                    <span
                        class="text-center"
                        :class="
                            data.item.deliveryOption === 'home'
                                ? 'text-info'
                                : ''
                        "
                    >
                        {{
                            $data.$deliveryOptionsCodes[
                                data.item.deliveryOption
                            ]
                        }}
                    </span>
                </template>
                <template #cell(paymentStatus)="data">
                    <span
                        class="text-center"
                        :class="
                            $data.$paymentStatusesMessageClass[
                                data.item.paymentStatus
                            ]
                        "
                    >
                        {{
                            $data.$paymentStatusesCodes[data.item.paymentStatus]
                        }}
                    </span>
                </template>
                <template #cell(paymentMethod)="data">
                    <span class="text-center">
                        {{
                            $data.$paymentMethodsCodes[data.item.paymentMethod]
                        }}
                    </span>
                </template>
                <template #cell(customer)="data">
                    <div
                        v-if="data.item.customer"
                        class="flex flex-col gap-1.5 py-1"
                    >
                        <div class="flex items-center gap-2">
                            <span
                                class="text-sm font-medium text-theme-primary"
                            >
                                {{ data.item.customer.firstName }}
                                {{ data.item.customer.lastName }}
                            </span>
                        </div>
                        <div class="flex items-center gap-1.5">
                            <span class="text-xs font-medium"> 📱 </span>
                            <span class="text-xs font-mono">
                                {{ data.item.customer.phone }}
                            </span>
                        </div>
                    </div>
                    <div v-else class="flex items-center gap-1.5 py-1">
                        <span
                            class="text-xs italic text-theme-muted bg-theme-hover px-2 py-1 rounded-md"
                        >
                            Sin cliente asociado
                        </span>
                    </div>
                </template>
                <template #cell(total)="data">
                    <div class="flex flex-col gap-2 py-1">
                        <div class="flex items-center gap-2">
                            <span
                                class="text-xs text-theme-muted font-medium w-12"
                            >
                                Total:
                            </span>
                            <span class="font-semibold">
                                {{ data.item.total | money }}
                            </span>
                        </div>
                        <div
                            v-if="data.item.valuePaid !== data.item.total"
                            class="flex items-center gap-2"
                        >
                            <span
                                class="text-xs text-theme-muted font-medium w-12"
                            >
                                Pagado:
                            </span>
                            <div class="flex items-center gap-1.5">
                                <span
                                    class="font-bold text-sm px-2 py-0.5 rounded-md"
                                    :class="{
                                        'bg-theme-success text-white':
                                            data.item.valuePaid >=
                                            data.item.total,
                                        'bg-theme-warning text-white':
                                            data.item.valuePaid > 0 &&
                                            data.item.valuePaid <
                                                data.item.total,
                                        'bg-theme-danger text-white':
                                            data.item.valuePaid === 0
                                    }"
                                >
                                    {{ data.item.valuePaid | money }}
                                </span>
                            </div>
                        </div>
                    </div>
                </template>
                <template #cell(deliveryAt)="data">
                    <small class="text-center">
                        {{ data.item.deliveryAt | dateSimple }}
                    </small>
                </template>
                <template #cell(createdAt)="data">
                    <small class="text-center">
                        {{ data.item.createdAt | date }}
                    </small>
                </template>
            </custom-sortable-table>
        </div>

        <app-dialog v-model="isOpenOrdersPreview" size="lg">
            <template #header>
                <div class="flex justify-between w-full">
                    <div></div>
                    <div
                        class="mx-4 mt-2 flex justify-center items-center flex-col"
                    >
                        <h3 class="text-xl text-theme-primary font-bold">
                            Vista Previa de Ordenes Seleccionadas
                        </h3>
                        <p class="text-sm mt-1">
                            {{ selected.length }} orden{{
                                selected.length !== 1 ? "es" : ""
                            }}
                            seleccionada{{ selected.length !== 1 ? "s" : "" }}
                        </p>
                    </div>
                    <div class="flex gap-2 mr-4">
                        <app-button
                            :loading="isGeneratingPDF"
                            color="brand-red"
                            @click="downloadPDFComponent(true)"
                        >
                            📄 PDF
                        </app-button>
                    </div>
                </div>
            </template>

            <div class="px-4 pb-4">
                <div class="max-h-600px overflow-y-auto">
                    <div
                        v-for="order in selected"
                        :key="order.id"
                        class="rounded-lg border border-theme-border my-4 shadow-sm hover:shadow-md transition-shadow"
                    >
                        <div class="px-4 py-3 rounded-t-lg border-b">
                            <div class="flex items-center justify-between">
                                <div class="flex items-center gap-2">
                                    <span
                                        class="text-xs font-medium text-theme-muted"
                                    >
                                        Orden
                                    </span>
                                    <span
                                        class="text-lg font-bold text-stack-primary"
                                    >
                                        #{{ order.id }}
                                    </span>
                                </div>
                                <div class="flex gap-2">
                                    <span
                                        class="px-2 py-1 text-xs font-semibold rounded-md"
                                        :class="
                                            $data.$ordersStatusesMessageClass[
                                                order.deliveryStatus
                                            ]
                                        "
                                    >
                                        {{
                                            $data.$ordersStatusesCodes[
                                                order.deliveryStatus
                                            ]
                                        }}
                                    </span>
                                    <span
                                        class="px-2 py-1 text-xs font-semibold rounded-md"
                                        :class="
                                            $data.$paymentStatusesMessageClass[
                                                order.paymentStatus
                                            ]
                                        "
                                    >
                                        {{
                                            $data.$paymentStatusesCodes[
                                                order.paymentStatus
                                            ]
                                        }}
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div class="p-4 space-y-3">
                            <div
                                class="flex items-start gap-2 py-3 px-2 rounded-md border-b"
                            >
                                <div class="flex-1">
                                    <span class="text-xs block mb-1">
                                        Cliente
                                    </span>
                                    <div
                                        v-if="order.customer"
                                        class="space-y-0.5"
                                    >
                                        <span class="text-sm font-medium block">
                                            {{ order.customer.firstName }}
                                            {{ order.customer.lastName }}
                                        </span>
                                        <span class="text-xs font-mono">
                                            📱 {{ order.customer.phone }}
                                        </span>
                                    </div>
                                    <span v-else class="text-xs italic">
                                        Sin cliente asociado
                                    </span>
                                </div>
                            </div>

                            <div class="grid grid-cols-2 gap-3">
                                <div class="col-span-2 rounded-lg p-3">
                                    <div
                                        class="flex justify-between items-center mb-2"
                                    >
                                        <span class="text-xs text-theme-muted">
                                            Total
                                        </span>
                                        <span
                                            class="text-lg font-bold text-theme-muted"
                                        >
                                            {{ order.total | money }}
                                        </span>
                                    </div>
                                    <div
                                        class="flex justify-between items-center"
                                    >
                                        <span class="text-xs text-theme-muted">
                                            Pagado
                                        </span>
                                        <span
                                            class="text-sm font-bold px-2 py-0.5 rounded-md"
                                            :class="{
                                                'bg-theme-success text-white':
                                                    order.valuePaid >=
                                                    order.total,
                                                'bg-theme-warning text-white':
                                                    order.valuePaid > 0 &&
                                                    order.valuePaid <
                                                        order.total,
                                                'bg-theme-danger text-white':
                                                    order.valuePaid === 0
                                            }"
                                        >
                                            {{ order.valuePaid | money }}
                                        </span>
                                    </div>
                                </div>

                                <div>
                                    <span
                                        class="text-xs text-theme-muted block mb-1"
                                    >
                                        Entrega
                                    </span>
                                    <span
                                        class="text-sm font-medium"
                                        :class="
                                            order.deliveryOption === 'home'
                                                ? 'text-theme-primary'
                                                : 'text-theme-muted'
                                        "
                                    >
                                        {{
                                            $data.$deliveryOptionsCodes[
                                                order.deliveryOption
                                            ]
                                        }}
                                    </span>
                                </div>

                                <div>
                                    <span
                                        class="text-xs text-theme-muted block mb-1"
                                    >
                                        Método Pago
                                    </span>
                                    <span
                                        class="text-sm font-medium text-theme-muted"
                                    >
                                        {{
                                            $data.$paymentMethodsCodes[
                                                order.paymentMethod
                                            ]
                                        }}
                                    </span>
                                </div>

                                <div>
                                    <span
                                        class="text-xs text-theme-muted block mb-1"
                                    >
                                        📆 Entrega
                                    </span>
                                    <span
                                        class="text-xs font-medium text-theme-muted"
                                    >
                                        {{ order.deliveryAt | dateSimple }}
                                    </span>
                                </div>

                                <div>
                                    <span
                                        class="text-xs text-theme-muted block mb-1"
                                    >
                                        📆 Creación
                                    </span>
                                    <span
                                        class="text-xs font-medium text-theme-muted"
                                    >
                                        {{ order.createdAt | date }}
                                    </span>
                                </div>

                                <div
                                    v-if="order.supplyStoreId"
                                    class="col-span-2"
                                >
                                    <span
                                        class="text-xs text-theme-muted block mb-1"
                                    >
                                        🏪 Tienda Recogida
                                    </span>
                                    <a
                                        :href="`/app/stores/detail/${order.supplyStoreId}`"
                                        target="_blank"
                                        rel="noopener"
                                        class="text-theme-base hover:text-theme-primary transition-colors hover:underline"
                                    >
                                        <span class="text-xs font-mono">
                                            {{ order.supplyStoreId }}
                                        </span>
                                    </a>
                                </div>

                                <div
                                    v-if="order.supplyStoreId"
                                    class="col-span-2"
                                >
                                    <span
                                        class="text-xs text-theme-muted block mb-1"
                                    >
                                        🏪 Tienda Suministro
                                    </span>
                                    <a
                                        :href="`/app/stores/detail/${order.supplyStoreId}`"
                                        target="_blank"
                                        rel="noopener"
                                        class="text-theme-base hover:text-theme-primary transition-colors hover:underline"
                                    >
                                        <span class="text-xs font-mono">
                                            {{ order.supplyStoreId }}
                                        </span>
                                    </a>
                                </div>

                                <div
                                    v-if="order.pickupWarehouseId"
                                    class="col-span-2"
                                >
                                    <span
                                        class="text-xs text-theme-muted block mb-1"
                                    >
                                        📦 Almacén Recogida
                                    </span>
                                    <a
                                        :href="`/app/warehouses/detail/${order.pickupWarehouseId}`"
                                        target="_blank"
                                        rel="noopener"
                                        class="text-theme-base hover:text-theme-primary transition-colors hover:underline"
                                    >
                                        <span class="text-xs font-mono">
                                            {{ order.pickupWarehouseId }}
                                        </span>
                                    </a>
                                </div>

                                <div
                                    v-if="order.supplyWarehouseId"
                                    class="col-span-2"
                                >
                                    <span
                                        class="text-xs text-theme-muted block mb-1"
                                    >
                                        📦 Almacén Suministro
                                    </span>
                                    <a
                                        :href="`/app/warehouses/detail/${order.supplyWarehouseId}`"
                                        target="_blank"
                                        rel="noopener"
                                        class="text-theme-base hover:text-theme-primary transition-colors hover:underline"
                                    >
                                        <span class="text-xs font-mono">
                                            {{ order.supplyWarehouseId }}
                                        </span>
                                    </a>
                                </div>
                            </div>

                            <div class="pt-2 border-t border-theme-border">
                                <div class="flex items-center justify-between">
                                    <span class="text-xs ttext-theme-muted">
                                        Última actualización
                                    </span>
                                    <span class="text-xs text-theme-muted">
                                        {{ order.updatedAt | date }}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </app-dialog>

        <order-creation-modal v-model="isCreationModalOpen" />
    </div>
</template>

<script>
import urlQuerySyncMixin from "@/mixins/urlQuerySyncMixin";
import { DateTime } from "luxon";
import { mapActions, mapGetters } from "vuex";

import { deliveryOptionsCodes } from "@/common/fields/delivery-options";
import { paymentMethodsCodes } from "@/common/fields/orders-payment-methods";
import {
    paymentStatusesCodes,
    paymentStatusesMessageClass
} from "@/common/fields/orders-payment-status-codes";
import {
    nextOrderStatuses,
    ordersStatusesCodes,
    ordersStatusesMessageClass
} from "@/common/fields/orders-status-codes";
import { downloadExcel } from "@/common/lib/excel";
import { generateOrdersListPDF } from "@/common/lib/pdf";
import { buildQueryFromUrl } from "@/common/lib/url-query-filters";

import OrderCreationModal from "@/components/orders/OrderCreationModal.vue";
import OrdersFilters from "@/components/orders/OrdersFilters.vue";
import AdvancedOptionsButton from "@/components/utils/AdvancedOptionsButton.vue";
import AppDialog from "@/components/utils/AppDialog.vue";
import CustomSortableTable from "@/components/utils/CustomSortableTable.vue";
import TooltipButton from "@/components/utils/TooltipButton.vue";

export default {
    name: "IndexOrder",
    components: {
        AdvancedOptionsButton,
        AppDialog,
        CustomSortableTable,
        OrderCreationModal,
        OrdersFilters,
        TooltipButton
    },
    mixins: [urlQuerySyncMixin],
    layout: "app",
    meta: {
        roles: ["administrator", "support"]
    },
    data: () => ({
        isCreationModalOpen: false,
        itemsCurrent: [],
        $ordersStatusesMessageClass: ordersStatusesMessageClass,
        $paymentStatusesMessageClass: paymentStatusesMessageClass,
        $paymentMethodsCodes: paymentMethodsCodes,
        $paymentStatusesCodes: paymentStatusesCodes,
        $ordersStatusesCodes: ordersStatusesCodes,
        $deliveryOptionsCodes: deliveryOptionsCodes,
        $enabledFilters: [
            "uuid",
            "deliveryAt",
            "createdAt",
            "origin",
            "customerId",
            "deliveryStatus",
            "paymentStatus",
            "sellerId",
            "supplyStoreId",
            "id"
        ],
        $fields: [
            { key: "id", label: "ID", sortable: false },
            {
                key: "deliveryStatus",
                label: "Estado",
                sortable: true
            },
            {
                key: "paymentStatus",
                label: "Estado de pago",
                sortable: true
            },
            {
                key: "deliveryOption",
                label: "Opción de entrega",
                sortable: true
            },
            {
                key: "paymentMethod",
                label: "Método de pago",
                sortable: true
            },
            {
                key: "customer",
                label: "Cliente",
                sortable: false
            },
            { key: "total", label: "Total", sortable: true },
            { key: "deliveryAt", label: "📆 Entrega", sortable: true },
            { key: "createdAt", label: "📆 Creación", sortable: true }
        ],
        currentPage: 1,
        pagesNumber: 0,
        itemsNumber: 0,
        pageSize: 24,
        sortConfig: { orderBy: "createdAt", orderType: "DESC" },
        loadedAllData: false,
        isLoadingAllData: false,
        itemsAll: [],
        selected: [],
        totalCalls: 1,
        isOpenOrdersPreview: false,
        isGeneratingPDF: false,
        query: {
            deliveryStatus: "",
            type: "web"
        },
        $urlPaginationKey: "page",
        $urlSortKey: "sort",
        $urlFilterSchema: [
            { key: "id", type: "string" },
            { key: "uuid", type: "string" },
            { key: "deliveryStatus", type: "string" },
            { key: "paymentStatus", type: "string" },
            { key: "origin", type: "string" },
            { key: "merchantId", type: "string" },
            { key: "merchantType", type: "string" },
            { key: "customerId", type: "number" },
            { key: "sellerId", type: "number" },
            { key: "createdAt", type: "range" },
            { key: "deliveryAt", type: "range" }
        ]
    }),
    computed: {
        ...mapGetters("control", ["backgroundColor"]),
        pageTitle() {
            return this.query.deliveryStatus === "quote"
                ? "Cotizaciones encontradas"
                : "Ordenes encontradas";
        },
        rowActions() {
            if (this.selected.length > 1) {
                return [
                    {
                        key: "open",
                        icon: "🔗",
                        label: "Abrir en nueva pestaña"
                    },
                    { key: "report", icon: "📕", label: "Generar PDF" }
                ];
            }

            const actions = [
                { key: "open", icon: "🔗", label: "Abrir en nueva pestaña" },
                { key: "preview", icon: "👀", label: "Vista previa" },
                {
                    key: "suppliers",
                    icon: "🚚",
                    label: "Dividir y asignar proveedores"
                }
            ];
            if (
                this.$ability.hasPermission("order:Update") &&
                this.selected.length > 0 &&
                this.selected[0].deliveryStatus !== "completed"
            ) {
                actions.push({
                    key: "complete",
                    icon: "🏁",
                    label: "Completar orden"
                });
            }
            if (this.$ability.hasPermission("order:Update")) {
                actions.push({
                    key: "next-status",
                    icon: "⏩",
                    label: "Siguiente estado"
                });
            }
            return actions;
        },
        sortOptions() {
            return [
                { label: "📆 Creación reciente", value: "createdAt:DESC" },
                { label: "📆 Creación antigua", value: "createdAt:ASC" },
                { label: "📆 Entrega reciente", value: "deliveryAt:DESC" },
                { label: "📆 Entrega antigua", value: "deliveryAt:ASC" },
                { label: "💰 Total mayor", value: "total:DESC" },
                { label: "💰 Total menor", value: "total:ASC" }
            ];
        },
        money() {
            const methods = [
                "cash",
                "bre_b",
                "daviplata",
                "nequi",
                "terminal",
                "terminal1",
                "terminal2",
                "terminal3"
            ];
            const result = { total: 0 };
            for (const item of this.itemsCurrent) {
                result.total = (result.total || 0) + (item.total || 0);
                const method = item.paymentMethod;
                if (methods.includes(method)) {
                    result[method] = (result[method] || 0) + (item.total || 0);
                }
            }
            return result;
        },
        advancedOptions() {
            const opts = [];
            if (this.itemsCurrent.length > 0) {
                opts.push({
                    key: "pdf",
                    icon: "📕",
                    label: "Descargar resultado PDF",
                    group: "action"
                });
            }
            if (this.$ability.hasPermission("order:ListAll")) {
                opts.push({
                    key: "download",
                    icon: "⏬",
                    label: "Descargar resultado",
                    group: "action"
                });
            }
            return opts;
        }
    },
    watch: {
        "$route.fullPath"(newPath, oldPath) {
            if (newPath === oldPath) return;
            this.query = {
                deliveryStatus: "",
                type: "web",
                ...buildQueryFromUrl(
                    this.$route.query,
                    this.$data.$urlFilterSchema
                )
            };
            this.reloadItems();
        }
    },
    async mounted() {
        const verifyPagePermission =
            this.$ability.verifyPagePermission.bind(this);
        if (!(await verifyPagePermission("order:List", "/app"))) return;

        try {
            const fetches = [];
            if (this.query.customerId) {
                fetches.push(
                    this.getCustomerById({ id: this.query.customerId })
                        .then((customer) => {
                            this.query = {
                                ...this.query,
                                customerObject: customer
                            };
                        })
                        .catch(() => {})
                );
            }
            if (this.query.sellerId) {
                fetches.push(
                    this.getUserById({ id: this.query.sellerId })
                        .then((user) => {
                            this.query = {
                                ...this.query,
                                sellerObject: user
                            };
                        })
                        .catch(() => {})
                );
            }
            if (fetches.length) await Promise.all(fetches);
        } catch {
            // no bloquea la carga
        }

        this.listItems(true);
    },
    beforeDestroy() {},
    methods: {
        ...mapActions("orders", [
            "listOrdersPaginated",
            "listAllOrders",
            "completeOrder",
            "nextOrderStatusesOrder"
        ]),
        ...mapActions("customers", ["getCustomerById"]),
        ...mapActions("users", ["getUserById"]),
        async listItems(complete = false) {
            this.isLoading = true;
            this.$loading.show();
            try {
                const { items, pagination } = await this.listOrdersPaginated({
                    ...this.query,
                    complete,
                    itemsByPage: this.pageSize,
                    orderBy: this.sortConfig.orderBy,
                    orderType: this.sortConfig.orderType,
                    page: this.currentPage
                });
                if (pagination) {
                    this.itemsNumber = pagination.itemsNumber;
                    this.pagesNumber = pagination.pagesNumber;
                }
                this.$nextTick(() => this.joinSelected(items));
                this.totalCalls = this.totalCalls + 1;
                this.$loading.hide();
            } catch (error) {
                this.$loading.hide();
                this.isLoading = false;
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        },
        async reloadItems() {
            this.currentPage = 1;
            await this.listItems(true);
        },
        onListSettingsChange({ pageSize, sortConfig }) {
            this.pageSize = pageSize;
            if (sortConfig) this.sortConfig = sortConfig;
            this.reloadItems();
        },
        onAdvancedOptionClick(key) {
            if (key === "pdf") this.downloadPDFComponent();
            if (key === "download") this.listAllItems();
        },
        async listAllItems() {
            this.isLoadingAllData = true;
            try {
                this.itemsAll = await this.listAllOrders(this.query);
                this.loadedAllData = true;
                const filename = `orders_${
                    this.currentPage
                }_${DateTime.now().toFormat("yyyy-MM-dd_HH-mm-ss")}`;
                downloadExcel({ rows: this.itemsAll, filename });
                this.isLoadingAllData = false;
            } catch (error) {
                this.isLoadingAllData = false;
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        },
        async completeOrderComponent() {
            const id = this.selected[0].id;

            const dialogResult = await this.$swal.fire({
                background: this.backgroundColor,
                icon: "info",
                title: `Vas a completar la orden ${id}`,
                html: `
            <div class="text-left space-y-4 text-sm">
                <p class="font-medium">
                    Al completar esta orden se realizarán las siguientes acciones:
                </p>

                <ul class="space-y-2">
                    <li class="flex items-start gap-2">
                        <span>📦</span>
                        <span>
                            Estado de entrega:
                            <strong class="text-theme-success">Completado</strong>
                        </span>
                    </li>

                    <li class="flex items-start gap-2">
                        <span>💰</span>
                        <span>
                            Estado de pago:
                            <strong class="text-theme-success">Completado</strong>
                        </span>
                    </li>

                    <li class="flex items-start gap-2">
                        <span>💵</span>
                        <span>
                            Valor pagado se ajusta al
                            <strong>total de la orden</strong>
                        </span>
                    </li>

                    <li class="flex items-start gap-2">
                        <span>📦</span>
                        <span>
                            Inventario:
                            <strong class="text-theme-warning">Se validará y descontará el stock</strong>
                        </span>
                    </li>
                </ul>

                <div class="bg-theme-hover border border-theme-border rounded-lg p-3 text-xs text-theme-base">
                    Esta acción es <strong>definitiva</strong> y no podrá deshacerse.
                </div>
            </div>
        `,
                showCancelButton: true,
                confirmButtonColor: "#02913f",
                cancelButtonColor: "#B38F00",
                confirmButtonText: "Sí, completar orden",
                cancelButtonText: "Cancelar"
            });
            if (!dialogResult.isConfirmed) return;

            this.$loading.show();

            try {
                const result = await this.completeOrder({
                    id
                });
                const index = this.itemsCurrent.findIndex(
                    (item) => item.id === id
                );
                if (index !== -1) {
                    this.itemsCurrent[index].deliveryStatus =
                        result.deliveryStatus;
                    this.itemsCurrent[index].paymentStatus =
                        result.paymentStatus;
                }
                this.$sound.play();
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: "Actualización exitosa",
                    icon: "success",
                    showCloseButton: true,
                    timer: 5000
                });
                this.$loading.hide();
                this.clearSelected();
            } catch (error) {
                this.$loading.hide();
                this.$sound.play("error");
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        },
        async nextOrderStatusesOrderComponent() {
            const order = this.selected[0];
            const currentStatus = order.deliveryStatus;
            const nextStatus = nextOrderStatuses[currentStatus];

            if (!nextStatus) {
                this.$swal.fire({
                    background: this.backgroundColor,
                    icon: "warning",
                    title: "No se puede avanzar",
                    text: `La orden con estado "${order.deliveryStatusMessage}" no puede avanzar a un siguiente estado.`
                });
                return;
            }

            const nextStatusLabel = ordersStatusesCodes[nextStatus];

            const dialogResult = await this.$swal.fire({
                background: this.backgroundColor,
                icon: "question",
                title: `Actualizar estado de la orden ${order.id}`,
                html: `<div class="text-left">
                    <p><strong>Estado actual:</strong> ${
                        ordersStatusesCodes[order.deliveryStatus]
                    }</p>
                    <p><strong>Siguiente estado:</strong> ${nextStatusLabel}</p>
                    <br/>
                    <p>¿Deseas continuar con esta actualización?</p>
                </div>`,
                showCancelButton: true,
                confirmButtonColor: "#02913f",
                cancelButtonColor: "#3085d6",
                confirmButtonText: "Si, actualizar",
                cancelButtonText: "Cancelar"
            });
            if (!dialogResult.isConfirmed) return;

            this.$loading.show();

            try {
                await this.nextOrderStatusesOrder({
                    id: order.id
                });

                const index = this.itemsCurrent.findIndex(
                    (item) => item.id === order.id
                );
                if (index !== -1) {
                    this.itemsCurrent[index].deliveryStatus = nextStatus;
                    this.itemsCurrent[index].deliveryStatusMessage =
                        nextStatusLabel;
                }

                this.$sound.play();
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: "Actualización exitosa",
                    html: `<div class="text-left">
                        <p><strong>Estado anterior:</strong> ${ordersStatusesCodes[currentStatus]}</p>
                        <p><strong>Estado nuevo:</strong> ${nextStatusLabel}</p>
                    </div>`,
                    icon: "success",
                    showCloseButton: true,
                    timer: 5000
                });
                this.$loading.hide();
            } catch (error) {
                this.$loading.hide();
                this.$sound.play("error");
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        },
        clearSelected() {
            this.$refs.selectableTable.clearSelected();
            this.selected = [];
        },
        goToRefactor() {
            const id = this.selected[0].id;
            window.open(`/app/orders/refactor/${id}`, "_blank").focus();
        },
        openPreview() {
            this.isOpenOrdersPreview = true;
        },
        joinSelected(items) {
            const itemsMap = new Map();

            this.selected.forEach((item) => {
                itemsMap.set(item.id, item);
            });
            items.forEach((item) => {
                if (!itemsMap.has(item.id)) {
                    itemsMap.set(item.id, item);
                }
            });

            this.itemsCurrent = Array.from(itemsMap.values());
            this.$refs.selectableTable.selectedIndex = this.selected.map(
                (_, index) => index
            );
            this.$refs.selectableTable.selectedRows = this.selected;
        },

        onRowAction({ action, item }) {
            switch (action) {
                case "open":
                    window.open(`/app/orders/detail/${item.id}`, "_blank");
                    break;
                case "suppliers":
                    this.goToRefactor();
                    break;
                case "complete":
                    this.completeOrderComponent();
                    break;
                case "next-status":
                    this.nextOrderStatusesOrderComponent();
                    break;
                case "preview":
                    this.openPreview();
                    break;
            }
        },
        onRowSelected(items) {
            const currentPageIds = new Set(this.itemsCurrent.map((p) => p.id));
            const itemsMap = new Map();
            this.selected.forEach((item) => {
                itemsMap.set(item.id, item);
            });
            for (const id of currentPageIds) {
                itemsMap.delete(id);
            }

            items.forEach((item) => {
                itemsMap.set(item.id, item);
            });

            this.selected = Array.from(itemsMap.values());
        },
        async downloadPDFComponent(useSelected = false) {
            this.isGeneratingPDF = true;
            try {
                await generateOrdersListPDF(
                    useSelected ? this.selected : this.itemsCurrent,
                    this.query
                );
            } catch (error) {
                this.$sound.play("error");
                await this.$swal.fire({
                    background: this.backgroundColor,
                    title: "Error al generar PDF",
                    text: error.message || "No se pudo generar el archivo PDF",
                    icon: "error"
                });
            } finally {
                this.isGeneratingPDF = false;
            }
        }
    }
};
</script>
